import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { api } from '@/utils/api';
import { toast } from 'sonner';
import OTPInput from '@/components/OTPInput';
import TermsModal from '@/components/TermsModal';
import {
  Phone, Mail, ArrowRight, Building2, TrendingUp,
  Shield, Eye, EyeOff, User, Lock,
  CheckCircle2, ChevronRight, ArrowLeft
} from 'lucide-react';

const STEPS = { FORM: 'form', OTP: 'otp' };

export default function AuthEnhanced({ setUser }) {
  const navigate = useNavigate();
  const [authMethod, setAuthMethod] = useState('email'); // 'phone' or 'email'
  const [isLogin, setIsLogin] = useState(true);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [signupStep, setSignupStep] = useState(STEPS.FORM);

  // OTP states
  const [otpSent, setOtpSent] = useState(false);
  const [otpTimer, setOtpTimer] = useState(0);
  const [emailOtp, setEmailOtp] = useState('');
  const [mobileOtp, setMobileOtp] = useState('');

  // T&C
  const [termsOpen, setTermsOpen] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);

  const [formData, setFormData] = useState({
    phone: '', email: '', password: '', name: '',
  });

  // Redirect if already logged in
  useEffect(() => {
    const token = localStorage.getItem('token');
    const savedUser = localStorage.getItem('user');
    if (!token || !savedUser) return;
    (async () => {
      try {
        await api.get('/auth/me');
        const role = JSON.parse(savedUser)?.role;
        navigate(role === 'admin' ? '/admin' : role === 'broker' ? '/broker' : '/investor', { replace: true });
      } catch {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
      }
    })();
  }, [navigate]);

  // ─── Post-auth redirect (checks for investment return URL) ───────
  const postAuthRedirect = (role) => {
    const returnTo = localStorage.getItem('investmentReturnTo');
    if (returnTo) {
      localStorage.removeItem('investmentReturnTo');
      setTimeout(() => { window.location.href = returnTo; }, 100);
      return;
    }
    const dest = role === 'admin' ? '/admin' : role === 'broker' ? '/broker' : '/investor';
    setTimeout(() => navigate(dest, { replace: true }), 100);
  };

  // OTP timer countdown
  useEffect(() => {
    if (otpTimer > 0) {
      const t = setTimeout(() => setOtpTimer(otpTimer - 1), 1000);
      return () => clearTimeout(t);
    }
  }, [otpTimer]);

  // ─── Sign-Up: Initiate (send dual OTP) ──────────────────────────────
  const handleSignup = async (e) => {
    e?.preventDefault();
    if (!termsAccepted) { toast.error('Please accept the Terms & Conditions'); return; }
    const mobile = formData.phone.replace(/\D/g, '');
    if (mobile.length !== 10) { toast.error('Enter a valid 10-digit mobile number'); return; }
    setLoading(true);
    try {
      const res = await api.post('/auth/register', {
        name: formData.name,
        email: formData.email,
        mobile: mobile,
        password: formData.password,
        role: 'investor',
        termsAccepted: true,
      });
      toast.success('OTPs sent to your email and mobile!');
      setSignupStep(STEPS.OTP);
      setOtpTimer(30);
      // Debug OTPs in mock mode
      if (res.data._debug) {
        console.log('[DEBUG] OTPs:', res.data._debug);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Registration failed');
    } finally { setLoading(false); }
  };

  // ─── Sign-Up: Verify dual OTPs ──────────────────────────────────────
  const handleVerifySignup = async () => {
    if (emailOtp.length !== 6 || mobileOtp.length !== 6) {
      toast.error('Please enter both 6-digit OTPs'); return;
    }
    setLoading(true);
    try {
      const res = await api.post('/auth/verify-signup', {
        email: formData.email,
        emailOtp, mobileOtp,
      });
      localStorage.setItem('token', res.data.access_token);
      localStorage.setItem('user', JSON.stringify(res.data.user));
      setUser(res.data.user);
      toast.success('Account created successfully!');
      postAuthRedirect('investor');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Verification failed');
    } finally { setLoading(false); }
  };

  // ─── Resend OTP ─────────────────────────────────────────────────────
  const handleResendOtp = async (channel) => {
    try {
      const res = await api.post('/auth/resend-otp', { email: formData.email, channel });
      toast.success(`${channel === 'email' ? 'Email' : 'Mobile'} OTP resent!`);
      setOtpTimer(30);
      if (res.data._debug) console.log('[DEBUG] Resend OTP:', res.data._debug);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to resend OTP');
    }
  };

  // ─── Mobile Login: Send OTP ─────────────────────────────────────────
  const handleMobileSendOtp = async () => {
    const mobile = formData.phone.replace(/\D/g, '');
    if (mobile.length !== 10) { toast.error('Enter a valid 10-digit mobile number'); return; }
    setLoading(true);
    try {
      const res = await api.post('/auth/mobile/send-otp', { mobile });
      setOtpSent(true);
      setOtpTimer(30);
      toast.success('OTP sent!');
      if (res.data._debug) console.log('[DEBUG] Mobile OTP:', res.data._debug);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send OTP');
    } finally { setLoading(false); }
  };

  // ─── Mobile Login: Verify OTP ───────────────────────────────────────
  const handleMobileVerifyOtp = async () => {
    const mobile = formData.phone.replace(/\D/g, '');
    if (mobileOtp.length !== 6) { toast.error('Enter a valid 6-digit OTP'); return; }
    setLoading(true);
    try {
      const res = await api.post('/auth/mobile/verify-otp', { mobile, otp: mobileOtp });
      localStorage.setItem('token', res.data.access_token);
      localStorage.setItem('user', JSON.stringify(res.data.user));
      setUser(res.data.user);
      toast.success('Welcome back!');
      const role = res.data.user?.role;
      postAuthRedirect(role);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'OTP verification failed');
    } finally { setLoading(false); }
  };

  // ─── Email Login ────────────────────────────────────────────────────
  const handleEmailLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await api.post('/auth/login', {
        email: formData.email, password: formData.password,
      });
      localStorage.setItem('token', res.data.access_token);
      localStorage.setItem('user', JSON.stringify(res.data.user));
      setUser(res.data.user);
      toast.success('Welcome back!');
      const role = res.data.user?.role;
      postAuthRedirect(role);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Invalid email or password');
    } finally { setLoading(false); }
  };

  // Reset when switching between login/signup
  const toggleMode = () => {
    setIsLogin(!isLogin);
    setSignupStep(STEPS.FORM);
    setOtpSent(false);
    setEmailOtp('');
    setMobileOtp('');
    setTermsAccepted(false);
  };

  const features = [
    { icon: Building2, title: 'Fractional Real Estate', desc: 'Invest in premium properties starting from ₹5 Lakhs', tag: 'Up to 45% ROI' },
    { icon: TrendingUp, title: 'Curated Opportunities', desc: 'RERA verified properties with proven developers', tag: 'From ₹5 Lakhs' },
    { icon: Shield, title: 'Secure & Transparent', desc: 'LLP-based ownership with full legal documentation', tag: '100% Compliant' },
  ];

  // ═══════════════════════════════════════════════════════════════════════
  // RENDER
  // ═══════════════════════════════════════════════════════════════════════

  return (
    <div className="min-h-screen bg-[#030303] flex">
      {/* Left: Auth Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8">
        <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} className="w-full max-w-md">
          <div className="mb-10">
            <Link to="/"><h1 className="text-2xl font-light tracking-wider text-white">ALT<span className="text-[#4ADE80]">SLICE</span></h1></Link>
          </div>

          <div className="mb-8">
            <h2 className="text-3xl font-light text-white mb-2" data-testid="auth-title">
              {isLogin ? 'Welcome back' : (signupStep === STEPS.OTP ? 'Verify your account' : 'Create your account')}
            </h2>
            <p className="text-gray-500">
              {isLogin ? 'Sign in to access your investments' :
                (signupStep === STEPS.OTP ? 'Enter the OTPs sent to your email and mobile' : 'Start your fractional real estate journey')}
            </p>
          </div>

          {/* Login: Auth method tabs */}
          {isLogin && (
            <div className="flex mb-8 border-b border-white/10">
              <button onClick={() => { setAuthMethod('phone'); setOtpSent(false); setMobileOtp(''); }}
                className={`flex-1 pb-4 text-sm font-medium flex items-center justify-center gap-2 transition-colors ${
                  authMethod === 'phone' ? 'text-[#4ADE80] border-b-2 border-[#4ADE80]' : 'text-gray-500 hover:text-gray-300'}`}
                data-testid="tab-phone-login">
                <Phone className="w-4 h-4" /> Mobile Login
              </button>
              <button onClick={() => setAuthMethod('email')}
                className={`flex-1 pb-4 text-sm font-medium flex items-center justify-center gap-2 transition-colors ${
                  authMethod === 'email' ? 'text-[#4ADE80] border-b-2 border-[#4ADE80]' : 'text-gray-500 hover:text-gray-300'}`}
                data-testid="tab-email-login">
                <Mail className="w-4 h-4" /> Email Login
              </button>
            </div>
          )}

          <AnimatePresence mode="wait">
            {/* ─── SIGNUP: Form Step ────────────────────────────────── */}
            {!isLogin && signupStep === STEPS.FORM && (
              <motion.form key="signup-form" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}
                onSubmit={handleSignup} className="space-y-5" data-testid="signup-form">
                <Field label="Full Name" icon={User}>
                  <input type="text" value={formData.name} onChange={e => setFormData({ ...formData, name: e.target.value })}
                    placeholder="As per your PAN card" required className="input-field" data-testid="signup-name" />
                </Field>
                <Field label="Email Address" icon={Mail}>
                  <input type="email" value={formData.email} onChange={e => setFormData({ ...formData, email: e.target.value })}
                    placeholder="you@example.com" required className="input-field" data-testid="signup-email" />
                </Field>
                <div>
                  <label className="text-xs text-gray-500 uppercase tracking-wider block mb-2">Mobile Number</label>
                  <div className="flex gap-2">
                    <div className="bg-white/5 border border-white/10 px-3 py-3 text-white flex items-center gap-1 text-sm shrink-0">
                      <span>+91</span>
                    </div>
                    <input type="tel" value={formData.phone}
                      onChange={e => setFormData({ ...formData, phone: e.target.value.replace(/\D/g, '').slice(0, 10) })}
                      placeholder="98765 43210" maxLength={10} required
                      className="flex-1 bg-white/5 border border-white/10 px-4 py-3 text-white placeholder:text-gray-600 focus:outline-none focus:border-[#4ADE80]/50 font-mono tracking-wider"
                      data-testid="signup-mobile" />
                  </div>
                </div>
                <Field label="Password" icon={Lock}>
                  <input type={showPassword ? 'text' : 'password'} value={formData.password}
                    onChange={e => setFormData({ ...formData, password: e.target.value })}
                    placeholder="Min 8 chars, 1 upper, 1 lower, 1 digit" required className="input-field pr-12" data-testid="signup-password" />
                  <button type="button" onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white">
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </Field>

                {/* T&C checkbox */}
                <div className="flex items-start gap-3">
                  <input type="checkbox" checked={termsAccepted} onChange={e => setTermsAccepted(e.target.checked)}
                    className="mt-1 accent-[#4ADE80]" data-testid="terms-checkbox" />
                  <p className="text-sm text-gray-400">
                    I agree to the{' '}
                    <button type="button" onClick={() => setTermsOpen(true)} className="text-[#4ADE80] hover:underline" data-testid="terms-link">
                      Terms & Conditions
                    </button>{' '}and{' '}
                    <Link to="/privacy" className="text-[#4ADE80] hover:underline">Privacy Policy</Link>
                  </p>
                </div>

                <button type="submit" disabled={loading || !termsAccepted}
                  className="w-full bg-[#4ADE80] hover:bg-[#4ADE80]/90 disabled:opacity-40 text-black font-semibold py-4 flex items-center justify-center gap-2 transition-all"
                  data-testid="signup-submit-btn">
                  {loading ? 'Sending OTPs...' : 'Create Account'}
                  <ArrowRight className="w-4 h-4" />
                </button>
              </motion.form>
            )}

            {/* ─── SIGNUP: OTP Verification Step ───────────────────── */}
            {!isLogin && signupStep === STEPS.OTP && (
              <motion.div key="signup-otp" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}
                className="space-y-6" data-testid="signup-otp-form">
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wider mb-3">Email OTP</p>
                  <OTPInput value={emailOtp} onChange={setEmailOtp} data-testid="email-otp-input" />
                  <div className="flex justify-end mt-2">
                    {otpTimer > 0 ? (
                      <span className="text-xs text-gray-500">Resend in {otpTimer}s</span>
                    ) : (
                      <button onClick={() => handleResendOtp('email')} className="text-xs text-[#4ADE80] hover:underline" data-testid="resend-email-otp">
                        Resend Email OTP
                      </button>
                    )}
                  </div>
                </div>
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wider mb-3">Mobile OTP</p>
                  <OTPInput value={mobileOtp} onChange={setMobileOtp} data-testid="mobile-otp-input" />
                  <div className="flex justify-end mt-2">
                    {otpTimer > 0 ? (
                      <span className="text-xs text-gray-500">Resend in {otpTimer}s</span>
                    ) : (
                      <button onClick={() => handleResendOtp('mobile')} className="text-xs text-[#4ADE80] hover:underline" data-testid="resend-mobile-otp">
                        Resend Mobile OTP
                      </button>
                    )}
                  </div>
                </div>
                <button onClick={handleVerifySignup} disabled={loading || emailOtp.length !== 6 || mobileOtp.length !== 6}
                  className="w-full bg-[#4ADE80] hover:bg-[#4ADE80]/90 disabled:opacity-40 text-black font-semibold py-4 flex items-center justify-center gap-2"
                  data-testid="verify-signup-btn">
                  {loading ? 'Verifying...' : 'Verify & Create Account'}
                  <ArrowRight className="w-4 h-4" />
                </button>
                <button onClick={() => { setSignupStep(STEPS.FORM); setEmailOtp(''); setMobileOtp(''); }}
                  className="w-full text-sm text-gray-500 hover:text-white flex items-center justify-center gap-1" data-testid="back-to-form-btn">
                  <ArrowLeft className="w-4 h-4" /> Back to form
                </button>
              </motion.div>
            )}

            {/* ─── LOGIN: Phone OTP ────────────────────────────────── */}
            {isLogin && authMethod === 'phone' && (
              <motion.div key="phone-login" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}
                className="space-y-6" data-testid="phone-login-form">
                {!otpSent ? (
                  <>
                    <div>
                      <label className="text-xs text-gray-500 uppercase tracking-wider block mb-2">Phone Number</label>
                      <div className="flex gap-2">
                        <div className="bg-white/5 border border-white/10 px-3 py-3 text-white flex items-center text-sm shrink-0">+91</div>
                        <input type="tel" value={formData.phone}
                          onChange={e => setFormData({ ...formData, phone: e.target.value.replace(/\D/g, '').slice(0, 10) })}
                          placeholder="Phone Number" maxLength={10}
                          className="flex-1 bg-white/5 border border-white/10 px-4 py-3 text-white placeholder:text-gray-600 focus:outline-none focus:border-[#4ADE80]/50 font-mono tracking-wider"
                          data-testid="login-phone" />
                      </div>
                    </div>
                    <button onClick={handleMobileSendOtp} disabled={loading || formData.phone.replace(/\D/g, '').length !== 10}
                      className="w-full bg-[#1a1a1a] hover:bg-[#252525] disabled:opacity-40 border border-white/10 text-white font-medium py-4 flex items-center justify-center gap-2"
                      data-testid="send-otp-btn">
                      {loading ? 'Sending OTP...' : 'Get OTP'} <ArrowRight className="w-4 h-4" />
                    </button>
                  </>
                ) : (
                  <>
                    <div>
                      <div className="flex items-center justify-between mb-3">
                        <label className="text-xs text-gray-500 uppercase tracking-wider">Enter OTP</label>
                        <span className="text-xs text-gray-500">Sent to +91 {formData.phone}</span>
                      </div>
                      <OTPInput value={mobileOtp} onChange={setMobileOtp} />
                    </div>
                    <button onClick={handleMobileVerifyOtp} disabled={loading || mobileOtp.length !== 6}
                      className="w-full bg-[#4ADE80] hover:bg-[#4ADE80]/90 disabled:opacity-40 text-black font-semibold py-4 flex items-center justify-center gap-2"
                      data-testid="verify-login-otp-btn">
                      {loading ? 'Verifying...' : 'Verify & Continue'} <ArrowRight className="w-4 h-4" />
                    </button>
                    <div className="text-center">
                      {otpTimer > 0 ? (
                        <span className="text-sm text-gray-500">Resend in <span className="text-white font-mono">{otpTimer}s</span></span>
                      ) : (
                        <button onClick={handleMobileSendOtp} className="text-sm text-[#4ADE80] hover:underline">Resend OTP</button>
                      )}
                    </div>
                    <button onClick={() => { setOtpSent(false); setMobileOtp(''); }} className="w-full text-sm text-gray-500 hover:text-white">
                      Change phone number
                    </button>
                  </>
                )}
              </motion.div>
            )}

            {/* ─── LOGIN: Email + Password ─────────────────────────── */}
            {isLogin && authMethod === 'email' && (
              <motion.form key="email-login" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}
                onSubmit={handleEmailLogin} className="space-y-6" data-testid="email-login-form">
                <Field label="Email Address" icon={Mail}>
                  <input type="email" value={formData.email} onChange={e => setFormData({ ...formData, email: e.target.value })}
                    placeholder="you@example.com" required className="input-field" data-testid="login-email" />
                </Field>
                <Field label="Password" icon={Lock}>
                  <input type={showPassword ? 'text' : 'password'} value={formData.password}
                    onChange={e => setFormData({ ...formData, password: e.target.value })}
                    placeholder="Enter your password" required className="input-field pr-12" data-testid="login-password" />
                  <button type="button" onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white">
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </Field>
                <div className="text-right">
                  <Link to="/forgot-password" className="text-sm text-gray-500 hover:text-[#4ADE80]">Forgot password?</Link>
                </div>
                <button type="submit" disabled={loading}
                  className="w-full bg-[#4ADE80] hover:bg-[#4ADE80]/90 disabled:opacity-40 text-black font-semibold py-4 flex items-center justify-center gap-2"
                  data-testid="login-submit-btn">
                  {loading ? 'Signing in...' : 'Sign In'} <ArrowRight className="w-4 h-4" />
                </button>
              </motion.form>
            )}
          </AnimatePresence>

          {/* Toggle login/signup */}
          <div className="mt-8 text-center">
            <button onClick={toggleMode} className="text-sm text-gray-500 hover:text-white" data-testid="toggle-auth-mode">
              {isLogin ? "Don't have an account? " : 'Already have an account? '}
              <span className="text-[#4ADE80]">{isLogin ? 'Sign up' : 'Sign in'}</span>
            </button>
          </div>

          <p className="mt-8 text-xs text-gray-600 text-center">
            By continuing, you agree to our{' '}
            <button onClick={() => setTermsOpen(true)} className="text-gray-400 hover:text-white">Terms of Service</button>
            {' '}and{' '}
            <Link to="/privacy" className="text-gray-400 hover:text-white">Privacy Policy</Link>
          </p>
        </motion.div>
      </div>

      {/* Right: Platform Preview */}
      <div className="hidden lg:flex w-1/2 bg-gradient-to-br from-[#0A0A0A] to-[#030303] border-l border-white/5 items-center justify-center p-12">
        <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.2 }} className="max-w-lg">
          <div className="mb-12">
            <h2 className="text-4xl font-light text-white mb-4">
              Invest in Premium<br /><span className="text-[#4ADE80]">Real Estate</span>
            </h2>
            <p className="text-gray-500 text-lg">Own a piece of India&apos;s finest properties with fractional ownership starting from just ₹5 Lakhs.</p>
          </div>
          <div className="space-y-4">
            {features.map((f, i) => {
              const Icon = f.icon;
              return (
                <motion.div key={i} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 + i * 0.1 }}
                  className="group bg-white/5 border border-white/10 hover:border-[#4ADE80]/30 p-5 transition-all">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-[#4ADE80]/10 flex items-center justify-center shrink-0"><Icon className="w-6 h-6 text-[#4ADE80]" /></div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-1">
                        <h3 className="text-white font-medium">{f.title}</h3>
                        <span className="text-xs bg-[#4ADE80]/20 text-[#4ADE80] px-2 py-0.5">{f.tag}</span>
                      </div>
                      <p className="text-sm text-gray-500">{f.desc}</p>
                    </div>
                    <ChevronRight className="w-5 h-5 text-gray-600 group-hover:text-[#4ADE80] transition-colors" />
                  </div>
                </motion.div>
              );
            })}
          </div>
          <div className="mt-12 grid grid-cols-3 gap-6">
            <div className="text-center"><p className="text-3xl font-light text-white mb-1">₹50Cr+</p><p className="text-xs text-gray-500 uppercase tracking-wider">Capital Raised</p></div>
            <div className="text-center border-x border-white/10"><p className="text-3xl font-light text-white mb-1">500+</p><p className="text-xs text-gray-500 uppercase tracking-wider">Investors</p></div>
            <div className="text-center"><p className="text-3xl font-light text-white mb-1">15+</p><p className="text-xs text-gray-500 uppercase tracking-wider">Properties</p></div>
          </div>
          <div className="mt-12 flex items-center justify-center gap-6">
            {['SEBI Compliant', 'RERA Verified', 'Bank-Grade Security'].map((t, i) => (
              <div key={i} className="flex items-center gap-2 text-gray-500"><CheckCircle2 className="w-4 h-4 text-[#4ADE80]" /><span className="text-xs">{t}</span></div>
            ))}
          </div>
        </motion.div>
      </div>

      <TermsModal open={termsOpen} onClose={() => setTermsOpen(false)}
        onAccept={() => { setTermsAccepted(true); setTermsOpen(false); }} role="investor" />

      {/* Inline input-field style */}
      <style>{`
        .input-field {
          width: 100%;
          background: rgba(255,255,255,0.05);
          border: 1px solid rgba(255,255,255,0.1);
          padding: 0.75rem 1rem 0.75rem 3rem;
          color: #fff;
          font-size: 0.95rem;
        }
        .input-field::placeholder { color: #444; }
        .input-field:focus { outline: none; border-color: rgba(74,222,128,0.5); }
      `}</style>
    </div>
  );
}

/** Field wrapper with icon + label */
function Field({ label, icon: Icon, children }) {
  return (
    <div>
      <label className="text-xs text-gray-500 uppercase tracking-wider block mb-2">{label}</label>
      <div className="relative">
        {Icon && <Icon className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />}
        {children}
      </div>
    </div>
  );
}
